/**
 * Synthesized instruments — all sounds built from oscillators + filters.
 *
 * Each instrument is a function that schedules notes on the Web Audio timeline.
 * No audio files are used; everything is procedural.
 */

/** Play a kick drum at the given time */
export function playKick(ctx: AudioContext, output: AudioNode, time: number, volume = 0.7): void {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();

  osc.type = 'sine';
  osc.frequency.setValueAtTime(150, time);
  osc.frequency.exponentialRampToValueAtTime(30, time + 0.12);

  gain.gain.setValueAtTime(volume, time);
  gain.gain.exponentialRampToValueAtTime(0.001, time + 0.3);

  osc.connect(gain);
  gain.connect(output);
  osc.start(time);
  osc.stop(time + 0.3);
}

/** Play a snare at the given time */
export function playSnare(ctx: AudioContext, output: AudioNode, time: number, volume = 0.4): void {
  // Noise burst through bandpass
  const bufferSize = ctx.sampleRate * 0.15;
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    data[i] = Math.random() * 2 - 1;
  }

  const noise = ctx.createBufferSource();
  noise.buffer = buffer;

  const filter = ctx.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = 3000;
  filter.Q.value = 0.7;

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(volume, time);
  gain.gain.exponentialRampToValueAtTime(0.001, time + 0.15);

  // Body tone
  const osc = ctx.createOscillator();
  const oscGain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(200, time);
  osc.frequency.exponentialRampToValueAtTime(80, time + 0.05);
  oscGain.gain.setValueAtTime(volume * 0.5, time);
  oscGain.gain.exponentialRampToValueAtTime(0.001, time + 0.1);

  noise.connect(filter);
  filter.connect(gain);
  gain.connect(output);
  osc.connect(oscGain);
  oscGain.connect(output);

  noise.start(time);
  noise.stop(time + 0.15);
  osc.start(time);
  osc.stop(time + 0.1);
}

/** Play a hi-hat at the given time */
export function playHiHat(ctx: AudioContext, output: AudioNode, time: number, open = false, volume = 0.2): void {
  const bufferSize = ctx.sampleRate * (open ? 0.2 : 0.05);
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    data[i] = Math.random() * 2 - 1;
  }

  const noise = ctx.createBufferSource();
  noise.buffer = buffer;

  const filter = ctx.createBiquadFilter();
  filter.type = 'highpass';
  filter.frequency.value = 8000;

  const gain = ctx.createGain();
  const decay = open ? 0.2 : 0.05;
  gain.gain.setValueAtTime(volume, time);
  gain.gain.exponentialRampToValueAtTime(0.001, time + decay);

  noise.connect(filter);
  filter.connect(gain);
  gain.connect(output);
  noise.start(time);
  noise.stop(time + decay);
}

/** Note frequency lookup (A minor scale, A1 through C7). */
const NOTE_FREQS: Record<string, number> = {
  'A1': 55, 'B1': 61.74, 'C2': 65.41, 'D2': 73.42, 'E2': 82.41, 'F2': 87.31, 'G2': 98,
  'A2': 110, 'B2': 123.47, 'C3': 130.81, 'D3': 146.83, 'E3': 164.81,
  'F3': 174.61, 'G3': 196, 'A3': 220, 'B3': 246.94, 'C4': 261.63,
  'D4': 293.66, 'E4': 329.63, 'F4': 349.23, 'G4': 392, 'A4': 440,
  'B4': 493.88, 'C5': 523.25, 'D5': 587.33, 'E5': 659.25,
  'F5': 698.46, 'G5': 783.99, 'A5': 880, 'B5': 987.77,
  'C6': 1046.5, 'D6': 1174.66, 'E6': 1318.51, 'F6': 1396.91, 'G6': 1567.98, 'A6': 1760,
};

export function noteFreq(note: string): number {
  return NOTE_FREQS[note] ?? 440;
}

/** Play a bass note (square/sawtooth through lowpass) */
export function playBass(
  ctx: AudioContext, output: AudioNode,
  time: number, freq: number, duration: number, volume = 0.35,
): void {
  const osc = ctx.createOscillator();
  const filter = ctx.createBiquadFilter();
  const gain = ctx.createGain();

  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(freq, time);

  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(400, time);
  filter.Q.value = 2;

  gain.gain.setValueAtTime(volume, time);
  gain.gain.setValueAtTime(volume, time + duration * 0.8);
  gain.gain.exponentialRampToValueAtTime(0.001, time + duration);

  osc.connect(filter);
  filter.connect(gain);
  gain.connect(output);
  osc.start(time);
  osc.stop(time + duration);
}

/** Play a lead note (sawtooth with filter sweep and vibrato) */
export function playLead(
  ctx: AudioContext, output: AudioNode,
  time: number, freq: number, duration: number, volume = 0.2,
): void {
  const osc = ctx.createOscillator();
  const filter = ctx.createBiquadFilter();
  const gain = ctx.createGain();

  // Vibrato LFO
  const vibrato = ctx.createOscillator();
  const vibratoGain = ctx.createGain();
  vibrato.type = 'sine';
  vibrato.frequency.value = 5;
  vibratoGain.gain.value = freq * 0.01;
  vibrato.connect(vibratoGain);
  vibratoGain.connect(osc.frequency);

  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(freq, time);

  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(800, time);
  filter.frequency.linearRampToValueAtTime(2000, time + duration * 0.3);
  filter.frequency.linearRampToValueAtTime(600, time + duration);
  filter.Q.value = 3;

  gain.gain.setValueAtTime(0.001, time);
  gain.gain.linearRampToValueAtTime(volume, time + 0.02);
  gain.gain.setValueAtTime(volume * 0.8, time + duration * 0.7);
  gain.gain.exponentialRampToValueAtTime(0.001, time + duration);

  osc.connect(filter);
  filter.connect(gain);
  gain.connect(output);
  osc.start(time);
  osc.stop(time + duration + 0.01);
  vibrato.start(time);
  vibrato.stop(time + duration + 0.01);
}

/** Play a pad chord (detuned sawtooths through lowpass) */
export function playPad(
  ctx: AudioContext, output: AudioNode,
  time: number, freqs: number[], duration: number, volume = 0.12,
): void {
  for (const freq of freqs) {
    for (const detune of [-6, 0, 6]) {
      const osc = ctx.createOscillator();
      const filter = ctx.createBiquadFilter();
      const gain = ctx.createGain();

      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(freq, time);
      osc.detune.value = detune;

      filter.type = 'lowpass';
      filter.frequency.value = 1200;
      filter.Q.value = 0.5;

      const noteVol = volume / freqs.length;
      gain.gain.setValueAtTime(0.001, time);
      gain.gain.linearRampToValueAtTime(noteVol, time + 0.3);
      gain.gain.setValueAtTime(noteVol, time + duration - 0.3);
      gain.gain.linearRampToValueAtTime(0.001, time + duration);

      osc.connect(filter);
      filter.connect(gain);
      gain.connect(output);
      osc.start(time);
      osc.stop(time + duration + 0.01);
    }
  }
}

/** Play an arp note (square wave, short and punchy) */
export function playArp(
  ctx: AudioContext, output: AudioNode,
  time: number, freq: number, duration: number, volume = 0.15,
): void {
  const osc = ctx.createOscillator();
  const filter = ctx.createBiquadFilter();
  const gain = ctx.createGain();

  osc.type = 'square';
  osc.frequency.setValueAtTime(freq, time);

  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(3000, time);
  filter.frequency.exponentialRampToValueAtTime(500, time + duration);
  filter.Q.value = 2;

  gain.gain.setValueAtTime(volume, time);
  gain.gain.setValueAtTime(volume * 0.6, time + duration * 0.5);
  gain.gain.exponentialRampToValueAtTime(0.001, time + duration);

  osc.connect(filter);
  filter.connect(gain);
  gain.connect(output);
  osc.start(time);
  osc.stop(time + duration + 0.01);
}
